import React, { useEffect, useRef, useState } from 'react';
import { notify } from '../common/AppToast';
import KkpEntrepriseAutocomplete from './KkpEntrepriseAutocomplete';
import {
  EMPTY_PLAN_DE_COM_CLIENT_FORM,
  getMissingBdcClientFields,
  mapDossierToClientForm,
  mergeDolibarrClientIntoForm,
} from '../../utils/planDeComClientInfo';
import {
  DEFAULT_BDC_OPTIONS,
  resolveBdcOptionsFromMeta,
} from '../../utils/planDeComBdcOptions';
import PlanDeComBdcOptionsFields from './PlanDeComBdcOptionsFields';
import kkpService from '../../services/kkpService';
import { AppButton, AppModal } from '../ui';
import '../../styles/kkp-planning-page.scss';

/**
 * Modal pour compléter les coordonnées client avant génération BDC / documents.
 */
const PlanDeComClientInfoModal = ({
  show,
  dossier = null,
  meta = null,
  title = 'Informations client',
  confirmLabel = 'Générer',
  confirmIcon = 'mdi:file-pdf-box',
  submitting = false,
  onClose,
  onConfirm,
}) => {
  const [form, setForm] = useState(EMPTY_PLAN_DE_COM_CLIENT_FORM);
  const [bdcOptionsForm, setBdcOptionsForm] = useState({ ...DEFAULT_BDC_OPTIONS });
  const [loadingDolibarr, setLoadingDolibarr] = useState(false);
  const wasOpenRef = useRef(false);
  const autofillRequestRef = useRef(0);
  const bdcChoices = resolveBdcOptionsFromMeta(meta);

  useEffect(() => {
    if (!show) {
      wasOpenRef.current = false;
      return undefined;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return undefined;

    const initial = mapDossierToClientForm(dossier);
    setForm(initial);
    setBdcOptionsForm({ ...bdcChoices.defaults });

    const thirdpartyId = Number(initial.dolibarr_thirdparty_id) || 0;
    if (thirdpartyId <= 0) return undefined;

    const requestId = ++autofillRequestRef.current;
    setLoadingDolibarr(true);
    kkpService.getDolibarrClient(thirdpartyId)
      .then((client) => {
        if (requestId !== autofillRequestRef.current) return;
        setForm((prev) => mergeDolibarrClientIntoForm(prev, client));
      })
      .catch(() => {})
      .finally(() => {
        if (requestId === autofillRequestRef.current) {
          setLoadingDolibarr(false);
        }
      });

    return undefined;
  }, [show, dossier]);

  if (!show) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEntrepriseSelect = ({
    id,
    nom,
    client_label: clientLabel,
    adresse,
    cp,
    ville,
    telephone,
    email,
  }) => {
    const companyName = String(nom ?? '').trim();
    const contactName = String(clientLabel ?? '').trim();
    setForm((prev) => ({
      ...prev,
      entreprise_label: companyName || prev.entreprise_label,
      dolibarr_thirdparty_id: id ?? null,
      client_label: contactName || companyName || prev.client_label,
      client_adresse: adresse || prev.client_adresse,
      client_cp: cp || prev.client_cp,
      client_ville: ville || prev.client_ville,
      client_telephone: telephone || prev.client_telephone,
      client_email: email || prev.client_email,
    }));
  };

  const handleEntrepriseResolving = (resolving) => {
    setLoadingDolibarr(resolving);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const missing = getMissingBdcClientFields(form);
    if (missing.length > 0) {
      notify.error('Renseignez au minimum l’entreprise, l’adresse, le code postal et la ville.');
      return;
    }
    onConfirm?.({ client: form, bdcOptions: bdcOptionsForm });
  };

  return (
    <AppModal
      show
      className="pdc-gen-modal"
      size="xl"
      centered
      scrollable
      onClose={onClose}
      closeDisabled={submitting}
      contentAs="form"
      contentProps={{ onSubmit: handleSubmit }}
      title={(
        <div>
          <div>{title}</div>
          <p className="pdc-gen-modal__lead mb-0">
            {loadingDolibarr
              ? 'Récupération des coordonnées Dolibarr…'
              : 'Vérifiez les informations, puis téléchargez.'}
          </p>
        </div>
      )}
      footer={(
        <>
          <AppButton size="sm" variant="outline-secondary" onClick={onClose} disabled={submitting}>Annuler</AppButton>
          <AppButton
            type="submit"
            size="sm"
            icon={submitting ? undefined : confirmIcon}
            loading={submitting}
            loadingLabel="Envoi…"
            disabled={submitting || loadingDolibarr}
          >
            {confirmLabel}
          </AppButton>
        </>
      )}
    >
            <div className="pdc-gen-modal__grid">
              <section className="pdc-gen-modal__section" aria-labelledby="pdc-gen-client-title">
                <h6 id="pdc-gen-client-title" className="pdc-gen-modal__section-title">Client</h6>
                <div className="pdc-gen-modal__fields">
                  <div className="pdc-gen-modal__row pdc-gen-modal__row--2">
                    <div>
                      <label className="form-label">
                        Entreprise <span className="text-danger">*</span>
                      </label>
                      <KkpEntrepriseAutocomplete
                        value={form.entreprise_label}
                        onSelect={handleEntrepriseSelect}
                        onResolvingChange={handleEntrepriseResolving}
                        inputClassName="form-control form-control-sm"
                        placeholder="Nom de l’entreprise"
                      />
                    </div>
                    <div>
                      <label className="form-label" htmlFor="pdc-gen-contact">Contact</label>
                      <input
                        id="pdc-gen-contact"
                        type="text"
                        className="form-control form-control-sm"
                        value={form.client_label}
                        onChange={(e) => updateField('client_label', e.target.value)}
                        placeholder={loadingDolibarr ? 'Recherche Dolibarr…' : 'Nom du contact'}
                        disabled={loadingDolibarr}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="form-label" htmlFor="pdc-gen-adresse">
                      Adresse <span className="text-danger">*</span>
                    </label>
                    <input
                      id="pdc-gen-adresse"
                      type="text"
                      className="form-control form-control-sm"
                      value={form.client_adresse}
                      onChange={(e) => updateField('client_adresse', e.target.value)}
                      placeholder="Numéro et rue"
                      disabled={loadingDolibarr}
                    />
                  </div>
                  <div className="pdc-gen-modal__row pdc-gen-modal__row--cp">
                    <div>
                      <label className="form-label" htmlFor="pdc-gen-cp">
                        CP <span className="text-danger">*</span>
                      </label>
                      <input
                        id="pdc-gen-cp"
                        type="text"
                        className="form-control form-control-sm"
                        value={form.client_cp}
                        onChange={(e) => updateField('client_cp', e.target.value)}
                        disabled={loadingDolibarr}
                      />
                    </div>
                    <div>
                      <label className="form-label" htmlFor="pdc-gen-ville">
                        Ville <span className="text-danger">*</span>
                      </label>
                      <input
                        id="pdc-gen-ville"
                        type="text"
                        className="form-control form-control-sm"
                        value={form.client_ville}
                        onChange={(e) => updateField('client_ville', e.target.value)}
                        disabled={loadingDolibarr}
                      />
                    </div>
                  </div>
                  <div className="pdc-gen-modal__row pdc-gen-modal__row--2">
                    <div>
                      <label className="form-label" htmlFor="pdc-gen-tel">Téléphone</label>
                      <input
                        id="pdc-gen-tel"
                        type="text"
                        className="form-control form-control-sm"
                        value={form.client_telephone}
                        onChange={(e) => updateField('client_telephone', e.target.value)}
                        disabled={loadingDolibarr}
                      />
                    </div>
                    <div>
                      <label className="form-label" htmlFor="pdc-gen-email">E-mail</label>
                      <input
                        id="pdc-gen-email"
                        type="email"
                        className="form-control form-control-sm"
                        value={form.client_email}
                        onChange={(e) => updateField('client_email', e.target.value)}
                        disabled={loadingDolibarr}
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="pdc-gen-modal__section" aria-labelledby="pdc-gen-doc-title">
                <h6 id="pdc-gen-doc-title" className="pdc-gen-modal__section-title">Conditions du document</h6>
                <PlanDeComBdcOptionsFields
                  options={bdcChoices}
                  values={bdcOptionsForm}
                  disabled={submitting}
                  onChange={setBdcOptionsForm}
                />
              </section>
            </div>
    </AppModal>
  );
};

export default PlanDeComClientInfoModal;
